import { Box, List, ListItem, ListItemButton, Tooltip } from "@mui/joy";
import DarkInput from "./DarkInput";
import { darkDropdownListSx, colorTokens, textTokens } from "../../styles/globalStyles";
import { SClose } from "../../styles/StyledMUI";

export type SearchResultItem = {
  id: number;
  label: string;
  /** Excursion booking workflow; present on excursion param picklists after GET when applicable. Stripped client-side before PUT. */
  booking_status?: string;
  /** When true, row is not selectable; shows conflict affordance. */
  disabled?: boolean;
  /** Tooltip when `disabled` (e.g. créneau occupé). */
  conflictHint?: string;
};

export type SearchInputWithDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  results: SearchResultItem[];
  onSelect: (item: SearchResultItem) => void;
  maxResults?: number;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
};

export default function SearchInputWithDropdown({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  results,
  onSelect,
  maxResults = 10,
  disabled = false,
  size = "sm",
}: SearchInputWithDropdownProps) {
  const showList = results.length > 0;
  const slice = results.slice(0, maxResults);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <DarkInput
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        size={size}
        disabled={disabled}
        sx={{ width: "100%", "& input": { color: textTokens.color.primary } }}
      />
      {showList && (
        <List size={size} sx={darkDropdownListSx}>
          {slice.map((item) => {
            const row = (
              <ListItemButton
                disabled={item.disabled === true}
                onClick={() => {
                  if (item.disabled) return;
                  onSelect(item);
                }}
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  width: "100%",
                }}
                aria-label={
                  item.disabled && item.conflictHint
                    ? `${item.label} — ${item.conflictHint}`
                    : item.label
                }
              >
                {item.disabled ? (
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontSize: "1.1rem",
                      display: "inline-flex",
                      lineHeight: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <SClose iconColor={colorTokens.status.danger} />
                  </Box>
                ) : null}
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: item.disabled ? 0.72 : 1,
                  }}
                >
                  {item.label}
                </Box>
              </ListItemButton>
            );
            return (
              <ListItem key={item.id}>
                {item.disabled && item.conflictHint ? (
                  <Tooltip title={item.conflictHint} placement="left" variant="soft">
                    <Box sx={{ width: "100%" }}>{row}</Box>
                  </Tooltip>
                ) : (
                  row
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}
